<template lang="pug">
  .width-100
    button.row.middle.m-bottom-2(@click='$emit("back")' v-if='!forProgram')
      button.m-right-05
        simple-svg.blue-icon(:filepath='leftArrow' width='20px' height='20px')
      p.text-normal.m-bottom-1.m-top-1.blue.bold {{ $t('shareResource.back') }}
    share-modal-filters.m-bottom-1(@search='filterUsers')
    share-modal-list(
      :source='forProgram ? "shareContentModal" : "shareResourceModal"'
      :sourceProps='forProgram ? shareContentModalProps : modalProps'
      @selectAll='toggleSelectAll'
      :users='usersList'
      :selectedUsers='selectedUsers'
      :loading='loadingUsers'
    )
      .user.row.middle(v-for='item in usersList' :key='item.id')
        checkbox.m-right-1(
          name='user'
          :checked='isUserSelected(item.id)'
          :updateCheck='() => toggleSelected(item)'
          transparent
        )
        img.avatar(v-if='!item.isGroup' :src='item.avatar || defaultProfileImage')
        .group-avatar.row.middle.center(v-else)
          simple-svg(:filepath='groupIcon' width='20px' height='20px')
        .column.m-left-1
          .row.middle.ellipsis.username
            h2.text-normal.m-right-05 {{ item.name }}
            h2.text-normal.ellipsis(
              v-if='!item.isGroup && (item.position.positionId || item.schoolName)'
              :title='getPositionWithSchoolName(item.position, item.schoolName)'
            ) {{ getPositionWithSchoolName(item.position, item.schoolName) }}
          h4.text-small.dark-gray(v-if='!item.isGroup') {{ item.email }}
        button.m-left-auto.m-right-1.black(
          v-if='item.isGroup'
          @click='() => showEditSharingGroupModal(item.id)'
        ) {{ $t('shareResource.editGroup') }}
    .row.end.middle.width-100
      span.error-desc(v-if='requiredTeachers') {{ $t('shareResource.requiredTeachers') }}
      button.primary-button.with-icon(type='button' @click='share')
        simple-svg.white-icon.m-right-1(:filepath='shareIcon' :width='"20px"' :height='"20px"')
        | {{ $t('shareResource.share') }}
</template>

<script>
import ShareModalFilters from '@/components/ShareModalFilters'
import ShareModalList from '@/components/ShareModalList'
import Checkbox from '@/components/Inputs/Checkbox'
import { deepCopy } from '../../../utils/object'
import defaultProfileImage from '@/assets/empty_profile.svg'
import groupIcon from '@/assets/ic_group.svg'
import shareIcon from '@/assets/ic_share.svg'
import amplitude from 'amplitude-js'
import { mapActions, mapState } from 'vuex'
import userPosition from '@/mixins/userPosition'
import leftArrow from '@/assets/ic_chevron-left.svg'
import { i18n } from '../../../main'

export default {
  mixins: [userPosition],
  components: { Checkbox, ShareModalFilters, ShareModalList },
  props: {
    forProgram: { type: Boolean }
  },
  data() {
    return {
      groupIcon,
      leftArrow,
      shareIcon,
      defaultProfileImage,
      requiredTeachers: false,
      loadingUsers: true,
      selectedUsers: []
    }
  },
  async created() {
    this.filters = { schoolId: this.userInfo.schoolId || null }
    await this.searchUsers(this.filters)
    await this.hydrateMySharingGroups()
    this.loadingUsers = false
  },
  computed: {
    ...mapState({
      users: state => state.userInfo.searchedUsers,
      mySharingGroups: state => state.sharingGroup.mySharingGroups,
      modalProps: state => state.behavior.shareResourceModal.props,
      shareContentModalProps: state => state.behavior.shareContentModal.props,
      program: state => state.educationalProgram.educationalProgram,
      resourceId: state => state.resource.resource.id,
      resourceName: state => state.resource.resource.title,
      innerLang: state => state.behavior.innerLocale,
      userInfo: state => state.userInfo.userInfo,
      resourceAuthor: state => state.resource.resource.author,
      locale: state => state.behavior.locale
    }),
    teacherLink() {
      return `${window.location.origin}/resource/${this.$route.params.id}`
    },
    usersList() {
      const list = [
        ...this.users.map(user => ({
          id: user.userId,
          name: user.fullName,
          avatar: user.avatar,
          position: user.position,
          schoolName: user.schoolName,
          email: user.email,
          isGroup: false
        })),
        ...this.mySharingGroups.map(group => ({
          id: group.id,
          name: group.name,
          users: group.userSharingGroups,
          isGroup: true
        }))
      ]
      return list.sort((a, b) => a.name.localeCompare(b.name))
    }
  },
  methods: {
    ...mapActions([
      'showElement',
      'hideElement',
      'hydrateMySharingGroups',
      'searchUsers',
      'shareResource',
      'shareContent'
    ]),
    toggleSelectAll() {
      if (this.selectedUsers.length === this.usersList.length) {
        this.selectedUsers = []
      } else {
        this.selectedUsers = deepCopy(this.usersList)
      }
    },
    isUserSelected(id) {
      return Boolean(this.selectedUsers.find(user => user.id === id))
    },
    toggleSelected(item) {
      if (this.isUserSelected(item.id)) {
        this.selectedUsers = this.selectedUsers.filter(user => user.id !== item.id)
      } else {
        this.selectedUsers = [...this.selectedUsers, item]
      }
    },
    resetSelectedUsers() {
      this.selectedUsers = []
    },
    showEditSharingGroupModal(groupId) {
      this.forProgram
        ? this.hideElement({ element: 'shareContentModal' })
        : this.hideElement({ element: 'shareResourceModal' })
      this.showElement({
        element: 'editSharingGroupModal',
        props: {
          source: this.forProgram ? 'shareContentModal' : 'shareResourceModal',
          sourceProps: this.forProgram ? this.shareContentModalProps : this.modalProps,
          groupId
        }
      })
    },
    async filterUsers(filters) {
      this.resetSelectedUsers()
      this.loadingUsers = true
      this.filters = filters
      await this.searchUsers(this.filters)
      this.loadingUsers = false
    },
    async share() {
      if (!this.selectedUsers.length) {
        this.requiredTeachers = true
      } else {
        this.requiredTeachers = false
        this.forProgram
          ? amplitude.getInstance().logEvent('Share Educational Content', {
            program: this.program.name,
            id: this.program.id
          })
          : amplitude.getInstance().logEvent('Share Resource', {
            resource: this.resourceName,
            id: this.resourceId
          })
        const { teacherLink, selectedUsers, resourceName, resourceId, locale, innerLang } = this
        const recipientIds = []
        for (const user of selectedUsers) {
          if (!user.isGroup) recipientIds.push(user.id)
          else for (const member of user.users) recipientIds.push(member.user.userId)
        }
        this.forProgram
          ? await this.shareContent({
            link: `${window.location.origin}/program/${this.program.id}`,
            educationalProgramName: this.program.name,
            educationalProgramId: this.program.id,
            educationalProgramLocale: innerLang,
            successMessage: i18n.t('shareProgram.doneModal'),
            locale,
            recipientIds: Array.from(new Set(recipientIds))
          })
          : await this.shareResource({
            resourceLink: teacherLink,
            recipientIds: Array.from(new Set(recipientIds)),
            resourceName,
            resourceId,
            successMessage: i18n.t('shareResource.doneModal'),
            locale,
            resourceLocale: innerLang
          })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.user {
  padding: 10px;
  height: 50px;
  .username{
    max-width: 450px;
  }
  .avatar{
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
  }
  .group-avatar{
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid $gray;
  }
}
</style>
